import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";

import {
  Card as MuiCard,
  CardHeader,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  TablePagination,
  TableContainer,
  Tooltip,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useTranslation } from "react-i18next";
import {
  useGetContractsQuery,
  useGetMyNotesQuery,
  useGetNotesForContractQuery,
} from "../../../../../redux/slices/indexApiSlice";
import { ContractStatus, IContract } from "../../../../../types/contract";

import { useNavigate } from "react-router-dom";
import { INote } from "../../../../../types/note";
import { format, isValid } from "date-fns";
import { grey, red, yellow } from "@mui/material/colors";
import { subPeriodFromDate } from "../../../../../utils/string";
import NoteDialog from "../../../../../components/dialogs/NoteDialog";
import { DialogMode } from "../../../../../types/dialogmode";
import { enGB, nl } from "date-fns/locale";

const Card = styled(MuiCard)(spacing);

const Paper = styled(MuiPaper)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
`;

interface TasksProps {
  contract?: IContract;
  defaultRowsPerPage?: number;
}

const Tasks: React.FC<TasksProps> = ({ contract, defaultRowsPerPage }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [events = [], setEvents] = useState<any>([]);
  const [contractEvents = [], setContractEvents] = useState<any>([]);
  const [noteEvents = [], setNoteEvents] = useState<any>([]);
  const [selectedNote, setSelectedNote] = useState<INote | null>(null);
  const [editNote, setEditNote] = useState<boolean>(false);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage || 5);

  const { data: contracts = [], isSuccess: contractsLoaded } =
    useGetContractsQuery(ContractStatus.ALL, {
      skip: contract?.id ? true : false, // skip if we are viewing a specific contract
    });

  const {
    data: notes = [], // reminders are notes with a reminderDate
    isSuccess: notesLoaded,
  } = useGetMyNotesQuery(true, {
    skip: contract?.id ? true : false, // skip if we are viewing a specific contract
  });

  const {
    data: notesForContract = [],
    isSuccess: notesForContractLoaded,
    refetch: refetchNotes,
  } = useGetNotesForContractQuery(contract?.id || "", {
    skip: !contract?.id,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (contract?.id) {
      refetchNotes();
    }
  }, [contract?.id]);

  useEffect(() => {
    if (!notesLoaded && !notesForContractLoaded) {
      return;
    }

    if (events?.length > 0) {
      return;
    }

    let contractTasksAsNote: any[] = [];
    if (contract?.id) {
      contractTasksAsNote =
        contract?.tasks?.map((task) => {
          return {
            title: task.details?.title,
            body: task.details?.body,
            start:
              task.details?.dueDate && isValid(new Date(task.details?.dueDate))
                ? new Date(task.details?.dueDate)
                : null,
            url: `/contract/${contract.id}`,
            color: grey[800],
          };
        }) || [];
    }

    const combinedEvents = [
      ...contractEvents,
      ...noteEvents,
      ...notesForContract,
      ...contractTasksAsNote,
    ];

    // sort the events by start date
    const sortedEvents = combinedEvents
      // .filter((e) => e?.start) // only include events with a start date
      .sort((a: any, b: any) => {
        if (!a?.start || !b?.start) {
          // then use the created date
          if (!a && !b) return 0;

          if (!a?.start && !b?.start) {
            return (
              new Date(b?.created).getTime() - new Date(a?.created).getTime()
            );
          } else if (!a?.start && a?.created) {
            return new Date(b.start).getTime() - new Date(a?.created).getTime();
          } else if (!b?.start && b?.created) {
            return new Date(b.created).getTime() - new Date(a.start).getTime();
          } else {
            return 0;
          }
        }

        return new Date(a.start).getTime() - new Date(b.start).getTime();
      });

    // setEvents(sortedEvents.filter((e) => e));
  }, [
    contractEvents,
    noteEvents,
    notesForContract,
    notesLoaded,
    notesForContractLoaded,
    contract?.tasks,
  ]);

  useEffect(() => {
    if (contractsLoaded && contracts.length > 0) {
      const noticeDateEvents = contracts
        ?.filter(
          (contract: IContract) =>
            contract.dates?.endDate && contract.noticePeriod
        )
        .map((contract: IContract) => {
          const noticeDate =
            contract.dates?.endDate && contract.noticePeriod
              ? subPeriodFromDate(
                  new Date(contract.dates.endDate),
                  contract.noticePeriod
                )
              : null;

          const unit = contract?.noticePeriod?.unit || "unknown";

          // do not include past dates
          if (noticeDate && new Date(noticeDate) < new Date()) {
            return null;
          }

          return {
            title: `${contract.name} ${t("expires in")} ${
              contract?.noticePeriod?.value
            } ${t(unit)}`,
            start:
              noticeDate && isValid(new Date(noticeDate))
                ? format(new Date(noticeDate), "yyyy-MM-dd")
                : null,
            url: `/contract/${contract.id}`,
            color: yellow[900],
          };
        });

      const endDateEvents = contracts?.map((contract: IContract) => {
        // do not include past dates
        if (
          contract.dates?.endDate &&
          new Date(contract.dates.endDate) < new Date()
        ) {
          return null;
        }

        return {
          title: `${contract.name} ${t("expires")}`,
          start:
            contract.dates?.endDate && isValid(new Date(contract.dates.endDate))
              ? format(new Date(contract.dates.endDate), "yyyy-MM-dd")
              : null,
          url: `/contract/${contract.id}`,
          color: red[700],
        };
      });

      setContractEvents([...noticeDateEvents, ...endDateEvents]);
    }
  }, [contracts, contractsLoaded]);

  useEffect(() => {
    if (notesLoaded && notes.length > 0) {
      const noteEvents = notes.map((note: INote) => {
        // do not include past dates
        if (
          note.reminderDate &&
          isValid(new Date(note.reminderDate)) &&
          new Date(note.reminderDate) < new Date()
        ) {
          return null;
        }

        return {
          id: note?.id,
          title: note.body,
          start:
            note?.reminderDate && isValid(new Date(note.reminderDate))
              ? format(new Date(note.reminderDate), "yyyy-MM-dd")
              : null,
          // url: note.contractId
          //   ? `/contract/${note.contractId}`
          //   : note.vendorId
          //   ? `/vendors/${note.vendorId}`
          //     : `/calendar/${note.id}`,
          url: `/calendar/${note.id}`,
          color: grey[800],
          created: note.created,
        };
      });

      setNoteEvents(noteEvents);
    }
  }, [notes, notesLoaded]);

  const handleNameClick = (event: any) => {
    if (event?.url && event.url !== "#") {
      navigate(event.url);
    } else {
      const note = notes.find((n: INote) => n.id === event.id);

      if (note) {
        setSelectedNote(note);
        setEditNote(true);
      }
    }
  };

  const handleCloseNoteDialog = () => {
    setEditNote(false);
    setSelectedNote(null);
  };

  // From: https://mui.com/material-ui/react-table/
  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - events.length) : 0;

  const visibleRows = React.useMemo(
    () => events.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [events, page, rowsPerPage]
  );

  return (
    <Card>
      <CardHeader
        // action={
        //   <IconButton aria-label="settings" size="large">
        //     <Alarm />
        //   </IconButton>
        // }
        action={
          <NoteDialog
            mode={DialogMode.Add}
            contract={contract}
            type="reminder"
          />
        }
        title={t("Reminders")}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("Due date")}</TableCell>
              <TableCell>{t("Name")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((event: any, i: number) => {
              return (
                <TableRow
                  key={event.id}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  onBlur={() => setHoveredIndex(-1)}
                >
                  <TableCell>
                    {event?.start && (
                      <Typography variant="body2">
                        {format(new Date(event.start), "PP", {
                          locale: i18n.language === "nl" ? nl : enGB,
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    onDoubleClick={handleNameClick}
                    sx={{
                      maxWidth: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Tooltip title={event.title}>
                      <Link
                        onClick={() => handleNameClick(event)}
                        sx={{ cursor: "pointer" }}
                      >
                        {event?.title || event?.body || t("No title")}
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <NoteDialog
                      mode={DialogMode.Edit}
                      note={event}
                      type="reminder"
                      iconOnly
                      showMenuIcon={i === hoveredIndex}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 33 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        // rowsPerPageOptions={[5, 10, 25]}
        rowsPerPageOptions={[]}
        component="div"
        count={events.length}
        rowsPerPage={rowsPerPage}
        page={page}
        hidden={events.length < rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {selectedNote && editNote && (
        <NoteDialog
          mode={DialogMode.Edit}
          type="reminder"
          note={selectedNote}
          controlled={true}
          open={editNote}
          handleClose={handleCloseNoteDialog}
        />
      )}
    </Card>
  );
};

export default Tasks;
